import React, { useState, useEffect, useMemo } from 'react';
import { repoDealsApi } from '../services/api';
import RecentDealsDateFilter from './RecentDealsDateFilter';
import { useRecentDealsFilterDate } from '../hooks/useRecentDealsFilterDate';
import { filterDealsByFilterDate } from '../utils/recentDealsDate';
import './RepoBlotter.css';

const RepoBlotter = ({ refreshTrigger, onViewDeal, onEditDeal, onDocsDeal }) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { filterDateYmd, setFilterDateYmd, loading: filterLoading } = useRecentDealsFilterDate();

  const fetchRecentDeals = async () => {
    try {
      setLoading(true);
      setError('');
      const recentDeals = await repoDealsApi.getAll();
      setDeals(recentDeals);
    } catch (err) {
      console.error('Error fetching recent repo deals:', err);
      setError('Failed to load recent repo deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentDeals();
  }, [refreshTrigger]);

  const filteredDeals = useMemo(
    () => filterDealsByFilterDate(deals, filterDateYmd),
    [deals, filterDateYmd]
  );

  const formatCurrency = (value) => {
    if (!value || value === null || value === undefined) return '0.00';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '0.00';
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === null || dateString === undefined) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB');
  };

  const canEditRejected = (deal) => {
    const status = String(deal?.approval_status || deal?.status || '').toLowerCase();
    const level = String(deal?.current_approval_level || '').toLowerCase();
    return status === 'rejected' || level === 'rejected';
  };

  if (loading && deals.length === 0) {
    return (
      <div className="repo-blotter">
        <RecentDealsDateFilter
          title="Recent Repo / Reverse Repo Deals"
          filterDateYmd={filterDateYmd}
          onFilterDateChange={setFilterDateYmd}
          onRefresh={fetchRecentDeals}
          refreshing={loading || filterLoading}
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading recent deals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="repo-blotter">
        <RecentDealsDateFilter
          title="Recent Repo / Reverse Repo Deals"
          filterDateYmd={filterDateYmd}
          onFilterDateChange={setFilterDateYmd}
          onRefresh={fetchRecentDeals}
          refreshing={loading}
        />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button type="button" onClick={fetchRecentDeals} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="repo-blotter">
      <RecentDealsDateFilter
        title="Recent Repo / Reverse Repo Deals"
        filterDateYmd={filterDateYmd}
        onFilterDateChange={setFilterDateYmd}
        onRefresh={fetchRecentDeals}
        refreshing={loading || filterLoading}
      />

      {filteredDeals.length === 0 ? (
        <div className="no-data-container">
          <p>No repo deals found for {filterDateYmd || 'the selected date'}.</p>
        </div>
      ) : (
        <div className="blotter-table-container">
          <table className="blotter-table">
            <thead>
              <tr>
                <th>Deal Number</th>
                <th>Deal Type</th>
                <th>Counterparty</th>
                <th>Trade Date</th>
                <th>Value Date</th>
                <th>Maturity Date</th>
                <th>Amount</th>
                <th>Rate (%)</th>
                <th>Tenor (Days)</th>
                <th>ISIN</th>
                <th>Face Value (Calc.)</th>
                <th>Face Value (Counterparty)</th>
                <th>Approval Level</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map((deal) => (
                <tr key={deal.id}>
                  <td className="deal-id">{deal.deal_number || deal.id}</td>
                  <td className="deal-type">
                    <span className={`deal-type-badge ${deal.deal_type?.toLowerCase() || 'unknown'}`}>
                      {deal.deal_type || 'Unknown'}
                    </span>
                  </td>
                  <td className="counterparty">{deal.counterparty_name || ''}</td>
                  <td className="date">{formatDate(deal.trade_date)}</td>
                  <td className="date">{formatDate(deal.value_date)}</td>
                  <td className="date">{formatDate(deal.maturity_date)}</td>
                  <td className="amount">{formatCurrency(deal.principal_amount)}</td>
                  <td className="rate">{deal.rate ? parseFloat(deal.rate).toFixed(2) : '0.00'}</td>
                  <td className="tenor">{deal.tenor || '0'}</td>
                  <td className="isin">{deal.isin_number || 'N/A'}</td>
                  <td className="amount">{formatCurrency(deal.face_value)}</td>
                  <td className="amount">{formatCurrency(deal.face_value_as_per_counterparty)}</td>
                  <td className="status">
                    <span className="status-badge status-default">
                      {deal.current_approval_level || 'front_office'}
                    </span>
                  </td>
                  <td className="date">{formatDate(deal.created_at)}</td>
                  <td className="actions">
                    <button type="button" className="btn btn-secondary" onClick={() => onViewDeal && onViewDeal(deal.id)} title="View deal">
                      View
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => onEditDeal && onEditDeal(deal.id)}
                      disabled={!canEditRejected(deal)}
                      style={{ marginLeft: 8, opacity: canEditRejected(deal) ? 1 : 0.5, cursor: canEditRejected(deal) ? 'pointer' : 'not-allowed' }}
                      title="Edit rejected deal"
                    >
                      Edit
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => onDocsDeal && onDocsDeal(deal.id)} style={{ marginLeft: 8 }} title="View/Upload documents">
                      Docs
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RepoBlotter;
